/**
 * Export Service
 * Orchestrates PDF export generation and audit logging
 */

import supabase from '../supabase/supabase';
import { getCaseById, getCaseTimeline, addCaseAuditEntry } from './caseService';
import { getStaffById, getStaffTimeline } from './staffService';
import { getWorkflowById } from './workflowService';
import {
  generateCaseSummaryPDF,
  generateCaseTimelinePDF,
  generateStaffTimelinePDF,
  generateWorkflowPDF,
} from '../utils/pdf/templates';
import { mergeDocumentsIntoPDF } from '../utils/pdf/documentMerger';

/**
 * Get current user info for export metadata
 */
const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

/**
 * Get tenant name for export header
 */
const getTenantName = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'Company';

    const { data: member } = await supabase
      .from('tenant_members')
      .select('tenant_id, tenants(name)')
      .eq('user_id', user.id)
      .single();

    return member?.tenants?.name || 'Company';
  } catch (error) {
    console.error('Error getting tenant name:', error);
    return 'Company';
  }
};

/**
 * Log export action to audit log
 */
const logExport = async (entityType, entityId, exportType, filename) => {
  try {
    const user = await getCurrentUser();
    const action = 'Document Exported';
    const details = `${exportType} PDF exported: ${filename}`;

    // Use appropriate audit log based on entity type
    if (entityType === 'case') {
      await addCaseAuditEntry(entityId, action, details);
    } else if (entityType === 'workflow') {
      // Workflow audit log via Edge Function
      await supabase.functions.invoke('api', {
        body: {
          path: 'workflow-audit',
          workflow_id: entityId,
          action,
          details,
        },
      });
    }
    // Staff doesn't have its own audit log, so we skip it
  } catch (error) {
    console.error('Error logging export:', error);
    // Don't throw - export should still succeed even if audit logging fails
  }
};

/**
 * Export Case Summary PDF
 * @param {string} caseId - Case UUID
 * @param {Object} options - Export options
 * @param {boolean} options.completedOnly - Only include completed actions
 * @param {boolean} options.includeDocuments - Include attached documents
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<string>} - Filename of exported PDF
 */
export const exportCaseSummary = async (caseId, options = {}) => {
  try {
    options.onProgress?.({ stage: 'loading', percent: 5, message: 'Loading case data...' });

    // Fetch case data
    const caseData = await getCaseById(caseId);
    if (!caseData) {
      throw new Error('Case not found');
    }

    // Get user and tenant info
    const user = await getCurrentUser();
    const tenantName = await getTenantName();

    options.onProgress?.({ stage: 'generating', percent: 20, message: 'Generating PDF...' });

    // Generate PDF (returns bytes if includeDocuments, otherwise downloads directly)
    const result = await generateCaseSummaryPDF(caseData, {
      completedOnly: options.completedOnly || false,
      anonymize: options.anonymize || false,
      exportedBy: user?.email || 'System',
      tenantName,
      returnBytes: options.includeDocuments || false,
    });

    let filename;

    // If including documents, merge them
    if (options.includeDocuments && caseData.case_documents?.length > 0) {
      options.onProgress?.({ stage: 'merging', percent: 40, message: 'Attaching documents...' });

      const mergedBytes = await mergeDocumentsIntoPDF(
        result.bytes,
        caseData.case_documents,
        options.onProgress
      );

      // Download merged PDF
      filename = result.filename;
      downloadPDFBytes(mergedBytes, filename);
    } else if (options.includeDocuments) {
      // No documents to merge, just download the generated PDF
      filename = result.filename;
      downloadPDFBytes(result.bytes, filename);
    } else {
      // Already downloaded by generateCaseSummaryPDF
      filename = result;
    }

    options.onProgress?.({ stage: 'complete', percent: 100, message: 'Export complete!' });

    // Log export
    await logExport('case', caseId, 'Case Summary', filename);

    return filename;
  } catch (error) {
    console.error('Error exporting case summary:', error);
    throw error;
  }
};

/**
 * Export Case Timeline PDF
 * @param {string} caseId - Case UUID
 * @param {Object} options - Export options
 * @param {boolean} options.includeDocuments - Include attached documents
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<string>} - Filename of exported PDF
 */
export const exportCaseTimeline = async (caseId, options = {}) => {
  try {
    options.onProgress?.({ stage: 'loading', percent: 5, message: 'Loading case data...' });

    // Fetch case data and timeline
    const [caseData, timelineEvents] = await Promise.all([
      getCaseById(caseId),
      getCaseTimeline(caseId),
    ]);

    if (!caseData) {
      throw new Error('Case not found');
    }

    // Get user and tenant info
    const user = await getCurrentUser();
    const tenantName = await getTenantName();

    options.onProgress?.({ stage: 'generating', percent: 20, message: 'Generating PDF...' });

    // Generate PDF
    const result = await generateCaseTimelinePDF(caseData, timelineEvents, {
      anonymize: options.anonymize || false,
      exportedBy: user?.email || 'System',
      tenantName,
      returnBytes: options.includeDocuments || false,
    });

    let filename;

    // If including documents, merge them
    if (options.includeDocuments && caseData.case_documents?.length > 0) {
      options.onProgress?.({ stage: 'merging', percent: 40, message: 'Attaching documents...' });

      const mergedBytes = await mergeDocumentsIntoPDF(
        result.bytes,
        caseData.case_documents,
        options.onProgress
      );

      filename = result.filename;
      downloadPDFBytes(mergedBytes, filename);
    } else if (options.includeDocuments) {
      filename = result.filename;
      downloadPDFBytes(result.bytes, filename);
    } else {
      filename = result;
    }

    options.onProgress?.({ stage: 'complete', percent: 100, message: 'Export complete!' });

    // Log export
    await logExport('case', caseId, 'Case Timeline', filename);

    return filename;
  } catch (error) {
    console.error('Error exporting case timeline:', error);
    throw error;
  }
};

/**
 * Export Staff Timeline PDF
 * @param {string} staffId - Staff UUID
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename of exported PDF
 */
export const exportStaffTimeline = async (staffId, options = {}) => {
  try {
    // Fetch staff data and timeline
    const [staffData, timelineEvents] = await Promise.all([
      getStaffById(staffId),
      getStaffTimeline(staffId),
    ]);

    if (!staffData) {
      throw new Error('Staff member not found');
    }

    // Get user and tenant info
    const user = await getCurrentUser();
    const tenantName = await getTenantName();

    // Generate PDF
    const filename = await generateStaffTimelinePDF(staffData, timelineEvents, {
      anonymize: options.anonymize || false,
      exportedBy: user?.email || 'System',
      tenantName,
    });

    // Log export (staff doesn't have audit log, but we try anyway)
    await logExport('staff', staffId, 'Staff Timeline', filename);

    return filename;
  } catch (error) {
    console.error('Error exporting staff timeline:', error);
    throw error;
  }
};

/**
 * Export Workflow PDF
 * @param {string} workflowId - Workflow UUID
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename of exported PDF
 */
export const exportWorkflow = async (workflowId, options = {}) => {
  try {
    // Fetch workflow data
    const workflowData = await getWorkflowById(workflowId);
    if (!workflowData) {
      throw new Error('Workflow not found');
    }

    // Get user and tenant info
    const user = await getCurrentUser();
    const tenantName = await getTenantName();

    // Generate PDF
    const filename = await generateWorkflowPDF(workflowData, {
      anonymize: options.anonymize || false,
      exportedBy: user?.email || 'System',
      tenantName,
    });

    // Log export
    await logExport('workflow', workflowId, 'Workflow', filename);

    return filename;
  } catch (error) {
    console.error('Error exporting workflow:', error);
    throw error;
  }
};

/**
 * Generic export handler - routes to appropriate export function
 * @param {string} entityType - 'case' | 'staff' | 'workflow'
 * @param {string} entityId - Entity UUID
 * @param {string} exportType - 'case_summary' | 'case_timeline' | 'staff_timeline' | 'workflow'
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename of exported PDF
 */
export const exportDocument = async (entityType, entityId, exportType, options = {}) => {
  switch (exportType) {
    case 'case_summary':
      return exportCaseSummary(entityId, options);
    case 'case_timeline':
      return exportCaseTimeline(entityId, options);
    case 'staff_timeline':
      return exportStaffTimeline(entityId, options);
    case 'workflow':
      return exportWorkflow(entityId, options);
    default:
      throw new Error(`Unknown export type: ${exportType}`);
  }
};

/**
 * Download PDF from bytes
 * @param {Uint8Array} bytes - PDF bytes
 * @param {string} filename - Filename for download
 */
const downloadPDFBytes = (bytes, filename) => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default {
  exportCaseSummary,
  exportCaseTimeline,
  exportStaffTimeline,
  exportWorkflow,
  exportDocument,
};
